import { createAsyncThunk } from '@reduxjs/toolkit'
import { taskStatusesAPI, boardColumnsAPI, columnStatusMappingsAPI } from '../../utils/supabase'

// Async Thunks for Task Statuses and Board Setup
export const fetchTaskStatuses = createAsyncThunk(
  'tasks/fetchTaskStatuses',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await taskStatusesAPI.getAllStatuses(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createTaskStatus = createAsyncThunk(
  'tasks/createTaskStatus',
  async ({ userId, statusData }, { rejectWithValue }) => {
    try {
      const { data, error } = await taskStatusesAPI.createStatus(userId, statusData)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateTaskStatus = createAsyncThunk(
  'tasks/updateTaskStatus',
  async ({ statusId, updates }, { rejectWithValue }) => {
    try {
      const { data, error } = await taskStatusesAPI.updateStatus(statusId, updates)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteTaskStatus = createAsyncThunk(
  'tasks/deleteTaskStatus',
  async (statusId, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const status = state.tasks.taskStatuses.find(s => s.id === statusId)

      // Protection 1: Cannot delete completion status
      if (status?.is_completion_status) {
        throw new Error('Cannot delete the completion status. The system requires a completion status to mark tasks as done.')
      }

      // Protection 2: Cannot delete status with tasks
      const tasksWithStatus = state.tasks.tasks.filter(task => task.status_id === statusId)
      if (tasksWithStatus.length > 0) {
        throw new Error(
          `Cannot delete status: ${tasksWithStatus.length} task(s) still use this status. ` +
          `Please move or delete these tasks first, or change their status.`
        )
      }

      const { error } = await taskStatusesAPI.deleteStatus(statusId)
      if (error) throw error
      return statusId
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchBoardColumns = createAsyncThunk(
  'tasks/fetchBoardColumns',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await boardColumnsAPI.getAllColumns(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchColumnStatusMappings = createAsyncThunk(
  'tasks/fetchColumnStatusMappings',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await columnStatusMappingsAPI.getAllMappings(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const initializeBoardSetup = createAsyncThunk(
  'tasks/initializeBoardSetup',
  async (userId, { rejectWithValue }) => {
    try {
      const { error } = await columnStatusMappingsAPI.initializeBoardSetup(userId)
      if (error) throw error
      return { success: true }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Board Column Management Thunks
export const createBoardColumn = createAsyncThunk(
  'tasks/createBoardColumn',
  async ({ userId, columnData }, { rejectWithValue }) => {
    try {
      const { data, error } = await boardColumnsAPI.createColumn(userId, columnData)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateBoardColumn = createAsyncThunk(
  'tasks/updateBoardColumn',
  async ({ columnId, updates }, { rejectWithValue }) => {
    try {
      const { data, error } = await boardColumnsAPI.updateColumn(columnId, updates)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteBoardColumn = createAsyncThunk(
  'tasks/deleteBoardColumn',
  async (columnId, { rejectWithValue }) => {
    try {
      const { error } = await boardColumnsAPI.deleteColumn(columnId)
      if (error) throw error
      return columnId
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateBoardColumns = createAsyncThunk(
  'tasks/updateBoardColumns',
  async (columns, { rejectWithValue }) => {
    try {
      const { data, error } = await boardColumnsAPI.updateColumns(columns)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Column Status Mapping Management Thunks
export const createColumnStatusMapping = createAsyncThunk(
  'tasks/createColumnStatusMapping',
  async ({ userId, mappingData }, { rejectWithValue }) => {
    try {
      const { data, error } = await columnStatusMappingsAPI.createMapping(userId, mappingData)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteColumnStatusMapping = createAsyncThunk(
  'tasks/deleteColumnStatusMapping',
  async (mappingId, { rejectWithValue }) => {
    try {
      const { error } = await columnStatusMappingsAPI.deleteMapping(mappingId)
      if (error) throw error
      return mappingId
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Regular reducers for optimistic updates
export const statusRegularReducers = {
  // Optimistic update for status modifications
  updateTaskStatusOptimistic: (state, action) => {
    const { statusId, updates } = action.payload
    const index = state.taskStatuses.findIndex(status => status.id === statusId)
    if (index !== -1) {
      state.taskStatuses[index] = { ...state.taskStatuses[index], ...updates }
    }
  },
  // Rollback optimistic updates on failure
  rollbackTaskStatusUpdate: (state, action) => {
    const { statusId, previousData } = action.payload
    const index = state.taskStatuses.findIndex(status => status.id === statusId)
    if (index !== -1) {
      state.taskStatuses[index] = previousData
    }
  }
}

// Status and Board reducers
export const statusReducers = {
  // Task Statuses
  [fetchTaskStatuses.pending]: (state) => {
    state.loading.taskStatuses = true
    state.error.taskStatuses = null
  },
  [fetchTaskStatuses.fulfilled]: (state, action) => {
    state.loading.taskStatuses = false
    state.taskStatuses = action.payload
    state.initialized.taskStatuses = true
  },
  [fetchTaskStatuses.rejected]: (state, action) => {
    state.loading.taskStatuses = false
    state.error.taskStatuses = action.payload
  },

  // Create Task Status
  [createTaskStatus.fulfilled]: (state, action) => {
    state.taskStatuses.push(action.payload)
  },

  // Update Task Status
  [updateTaskStatus.fulfilled]: (state, action) => {
    const index = state.taskStatuses.findIndex(status => status.id === action.payload.id)
    if (index !== -1) {
      state.taskStatuses[index] = action.payload
    }
  },

  // Delete Task Status
  [deleteTaskStatus.fulfilled]: (state, action) => {
    state.taskStatuses = state.taskStatuses.filter(status => status.id !== action.payload)
  },

  // Board Columns
  [fetchBoardColumns.pending]: (state) => {
    state.loading.boardColumns = true
    state.error.boardColumns = null
  },
  [fetchBoardColumns.fulfilled]: (state, action) => {
    state.loading.boardColumns = false
    state.boardColumns = action.payload
    state.initialized.boardColumns = true
  },
  [fetchBoardColumns.rejected]: (state, action) => {
    state.loading.boardColumns = false
    state.error.boardColumns = action.payload
  },

  // Column Status Mappings
  [fetchColumnStatusMappings.pending]: (state) => {
    state.loading.columnStatusMappings = true
    state.error.columnStatusMappings = null
  },
  [fetchColumnStatusMappings.fulfilled]: (state, action) => {
    state.loading.columnStatusMappings = false
    state.columnStatusMappings = action.payload
    state.initialized.columnStatusMappings = true
  },
  [fetchColumnStatusMappings.rejected]: (state, action) => {
    state.loading.columnStatusMappings = false
    state.error.columnStatusMappings = action.payload
  },

  // Board Initialization
  [initializeBoardSetup.fulfilled]: () => {
    // Board setup completed - actual data will be fetched separately
  },
  [initializeBoardSetup.rejected]: (state, action) => {
    state.error.boardColumns = action.payload
  },

  // Board Column Management
  [createBoardColumn.fulfilled]: (state, action) => {
    state.boardColumns.push(action.payload)
  },
  [updateBoardColumn.fulfilled]: (state, action) => {
    const index = state.boardColumns.findIndex(col => col.id === action.payload.id)
    if (index !== -1) {
      state.boardColumns[index] = action.payload
    }
  },
  [deleteBoardColumn.fulfilled]: (state, action) => {
    state.boardColumns = state.boardColumns.filter(col => col.id !== action.payload)
  },
  [updateBoardColumns.fulfilled]: (state, action) => {
    state.boardColumns = action.payload
  },

  // Column Status Mapping Management
  [createColumnStatusMapping.fulfilled]: (state, action) => {
    state.columnStatusMappings.push(action.payload)
  },
  [deleteColumnStatusMapping.fulfilled]: (state, action) => {
    state.columnStatusMappings = state.columnStatusMappings.filter(mapping => mapping.id !== action.payload)
  }
}
